import type { BilingualLabel } from '@/i18n';

/**
 * What each setting looks like as a form.
 *
 * Declarative on purpose: adding a field means adding a line here, not writing
 * another form. It is also what keeps the JSON editor away from operators —
 * anything described here renders as real inputs, and only settings nobody has
 * described yet fall through to the advanced section.
 */

export type FieldKind =
  /** One value, same in both languages: an email, a URL, a slug. */
  | 'text'
  /** A short value in Arabic *and* English, shown as a pair. */
  | 'bilingual'
  /** The same, with room for a paragraph. */
  | 'bilingualText'
  | 'number'
  /** Money typed in riyals, stored in halalas. */
  | 'money'
  | 'toggle'
  | 'select'
  /** Days of the week, stored as numbers (0 = Sunday). */
  | 'weekdays'
  /** A list of short strings, typed one per line. */
  | 'lines'
  /** Products picked and ordered by hand, stored as their ids (R7). */
  | 'products'
  /** Answers the store writes: a code and a label in each language (R8). */
  | 'choices'
  /** "How much do I need?" — a unit, labelled amounts, a note (R15). */
  | 'quantityGuide'
  /** CSS or a script, typed as written: monospace, left to right (ADR-092). */
  | 'code'
  /** The points programme's tiers: names, a year's spend, an earn rate (ADR-115). */
  | 'tiers';

export interface FieldSchema {
  /** Key inside the setting's JSON object. */
  name: string;
  label: BilingualLabel;
  kind: FieldKind;
  hint?: BilingualLabel;
  /** For `select`. */
  options?: { value: string; label: BilingualLabel }[];
  placeholder?: BilingualLabel;
}

export interface SectionSchema {
  /** The settings key this section edits. */
  key: string;
  title: BilingualLabel;
  subtitle?: BilingualLabel;
  fields: FieldSchema[];
}

/**
 * The settings an operator edits, in the order they matter.
 *
 * Deliberately short: everything here is something a store owner changes, not
 * something a developer configures. Provider credentials and infrastructure
 * live in environment variables and are refused by the API outright (ADR-013).
 */
export const SETTING_SECTIONS: SectionSchema[] = [
  {
    key: 'store.info',
    title: { ar: 'هوية المتجر', en: 'Store identity' },
    subtitle: { ar: 'الاسم وبيانات التواصل الظاهرة للعملاء', en: 'The name and contact details customers see' },
    fields: [
      { name: 'name', label: { ar: 'اسم المتجر', en: 'Store name' }, kind: 'text' },
      { name: 'supportEmail', label: { ar: 'بريد الدعم', en: 'Support email' }, kind: 'text' },
      { name: 'phone', label: { ar: 'رقم التواصل', en: 'Contact phone' }, kind: 'text' },
      {
        name: 'warrantyNote',
        label: { ar: 'الضمان', en: 'Warranty' },
        kind: 'bilingual',
        hint: {
          ar: 'سطر واحد يظهر تحت زر الشراء — مثل «ضمان الخياطة سنة كاملة». اتركه فارغًا لإخفائه',
          en: 'One line under the buy button — e.g. "Stitching guaranteed for a year". Empty hides it',
        },
      },
      {
        name: 'defaultLocale',
        label: { ar: 'اللغة الافتراضية', en: 'Default language' },
        kind: 'select',
        options: [
          { value: 'ar', label: { ar: 'العربية', en: 'Arabic' } },
          { value: 'en', label: { ar: 'الإنجليزية', en: 'English' } },
        ],
      },
      { name: 'defaultCurrency', label: { ar: 'العملة', en: 'Currency' }, kind: 'text' },
    ],
  },
  {
    // The owner's own CSS and scripts (ADR-092).
    key: 'storefront.code',
    title: { ar: 'أكوادك الخاصة', en: 'Your own code' },
    subtitle: {
      ar: 'CSS وسكربتات تعمل في كل صفحات المتجر — لأداة محادثة أو وسم تحقق أو تعديل في الشكل',
      en: 'CSS and scripts on every storefront page — for a chat widget, a verification tag, or a touch to the look',
    },
    fields: [
      {
        name: 'enabled',
        label: { ar: 'مفعّلة', en: 'In use' },
        kind: 'toggle',
        hint: { ar: 'أوقفها لإزالتها كلها فورًا إن تعطّل شيء', en: 'Switch off to take all of it off the store at once if something breaks' },
      },
      { name: 'css', label: { ar: 'CSS', en: 'CSS' }, kind: 'code', hint: { ar: 'يُطبَّق بعد القالب والهوية', en: 'Applied after the theme and Branding' } },
      {
        name: 'headScript',
        label: { ar: 'سكربت في الرأس (head)', en: 'Script in the head' },
        kind: 'code',
        hint: { ar: 'JavaScript فقط، دون وسم <script>', en: 'JavaScript only, without the <script> tag' },
      },
      {
        name: 'bodyScript',
        label: { ar: 'سكربت في آخر الصفحة', en: 'Script at the end of the page' },
        kind: 'code',
        hint: { ar: 'JavaScript فقط، دون وسم <script>', en: 'JavaScript only, without the <script> tag' },
      },
      {
        name: 'allowedOrigins',
        label: { ar: 'المواقع المسموح بها', en: 'Allowed origins' },
        kind: 'lines',
        hint: {
          ar: 'سطر لكل موقع تُحمّل منه أكوادك أو تتصل به، مثل https://widget.example.com — وغيرها يمنعه المتصفح',
          en: 'One per line: each site your code loads from or talks to, like https://widget.example.com — the browser blocks any other',
        },
      },
    ],
  },
  {
    // Closed for maintenance (ADR-071).
    key: 'store.maintenance',
    title: { ar: 'إغلاق المتجر للصيانة', en: 'Closed for maintenance' },
    subtitle: {
      ar: 'يرى الزوار رسالتك بدل المتجر، ولا يُقبل طلب حتى تفتحه',
      en: 'Visitors see your message instead of the store, and no order is taken until you reopen',
    },
    fields: [
      { name: 'enabled', label: { ar: 'المتجر مغلق', en: 'The store is closed' }, kind: 'toggle' },
      {
        name: 'message',
        label: { ar: 'رسالة للزوار', en: 'Message to visitors' },
        kind: 'bilingual',
        hint: { ar: 'مثل «نعود قريبًا بتشكيلة جديدة»', en: 'Like "Back soon with a new collection"' },
      },
      {
        name: 'previewCode',
        label: { ar: 'عبارة المعاينة', en: 'Preview phrase' },
        kind: 'text',
        hint: {
          ar: 'من يفتح المتجر بها يراه وهو مغلق — زر «عرض المتجر» يحملها. اختر عبارة لا كلمة',
          en: 'Whoever opens the store with it sees it while closed — "View store" carries it. Choose a phrase, not a word',
        },
      },
    ],
  },
  {
    key: 'tax.config',
    title: { ar: 'الضريبة', en: 'Tax' },
    fields: [
      {
        name: 'rateBasisPoints',
        label: { ar: 'نسبة الضريبة', en: 'Tax rate' },
        kind: 'number',
        hint: { ar: 'بنقاط الأساس: ١٥٠٠ تعني ١٥٪', en: 'In basis points: 1500 means 15%' },
      },
      {
        name: 'pricesIncludeTax',
        label: { ar: 'الأسعار شاملة الضريبة', en: 'Prices include tax' },
        kind: 'toggle',
        hint: {
          ar: 'الضريبة محتواة داخل السعر المعروض، لا تُضاف عليه',
          en: 'Tax is contained in the displayed price, not added on top',
        },
      },
      { name: 'label', label: { ar: 'اسم الضريبة', en: 'Tax label' }, kind: 'bilingual' },
    ],
  },
  {
    key: 'order.policy',
    title: { ar: 'سياسة الطلبات', en: 'Order policy' },
    subtitle: { ar: 'الترقيم وما يقبله المتجر', en: 'Numbering and what the store accepts' },
    fields: [
      {
        name: 'numberPrefix',
        label: { ar: 'بادئة رقم الطلب', en: 'Order number prefix' },
        kind: 'text',
        hint: { ar: 'لا يُعيد ترقيم الطلبات السابقة', en: 'Does not renumber existing orders' },
      },
      {
        name: 'minimumOrderTotal',
        label: { ar: 'الحد الأدنى للطلب', en: 'Minimum order total' },
        kind: 'money',
        hint: { ar: 'صفر يعني بلا حد أدنى', en: 'Zero means no minimum' },
      },
      {
        name: 'maxQuantityPerLine',
        label: { ar: 'أقصى كمية للصنف', en: 'Max quantity per item' },
        kind: 'number',
      },
      {
        name: 'maxSamplesPerOrder',
        label: { ar: 'أقصى عدد للعيّنات في الطلب', en: 'Samples per order, at most' },
        kind: 'number',
        hint: {
          ar: 'كل العيّنات معًا، من أي منتج — «ثلاث عيّنات». صفر يعني بلا حد (R16)',
          en: 'All samples together, of any product — "three swatches". Zero means no cap (R16)',
        },
      },
    ],
  },
  {
    key: 'inventory.policy',
    title: { ar: 'سياسة المخزون', en: 'Inventory policy' },
    fields: [
      {
        name: 'lowStockThreshold',
        label: { ar: 'حد المخزون المنخفض', en: 'Low stock threshold' },
        kind: 'number',
        hint: {
          ar: 'يظهر في لوحة التحكم وشاشة المخزون معًا',
          en: 'Drives both the dashboard count and the inventory screen',
        },
      },
      {
        name: 'hideOutOfStock',
        label: { ar: 'إخفاء المنتجات غير المتوفرة', en: 'Hide out-of-stock products' },
        kind: 'toggle',
      },
    ],
  },
  {
    key: 'payments.config',
    title: { ar: 'الدفع عند الاستلام', en: 'Cash on delivery' },
    subtitle: {
      ar: 'رسوم التحصيل وحدود قيمة الطلب',
      en: 'The handling fee and the order-value window',
    },
    fields: [
      {
        name: 'codFee',
        label: { ar: 'رسوم الدفع عند الاستلام', en: 'Cash-on-delivery fee' },
        kind: 'money',
        hint: {
          ar: 'تُضاف كبند مستقل ويراها العميل قبل التأكيد. صفر يعني بلا رسوم.',
          en: 'Charged as its own line and shown before the customer confirms. Zero means no fee.',
        },
      },
      {
        name: 'codMinimumOrder',
        label: { ar: 'أقل قيمة للدفع عند الاستلام', en: 'Minimum for cash on delivery' },
        kind: 'money',
        hint: { ar: 'صفر يعني بلا حد أدنى', en: 'Zero means no minimum' },
      },
      {
        name: 'codMaximumOrder',
        label: { ar: 'أعلى قيمة للدفع عند الاستلام', en: 'Maximum for cash on delivery' },
        kind: 'money',
        hint: {
          ar: 'صفر يعني بلا حد أعلى — يُقاس على الإجمالي الذي يُحصّله المندوب',
          en: 'Zero means no ceiling — measured on the total the driver collects',
        },
      },
    ],
  },
  {
    key: 'auth.login',
    title: { ar: 'تسجيل الدخول', en: 'Sign-in' },
    subtitle: {
      ar: 'كيف يدخل العملاء والموظفون إلى حساباتهم',
      en: 'How customers and staff get into their accounts',
    },
    fields: [
      {
        name: 'methods',
        label: { ar: 'طريقة الدخول', en: 'Sign-in method' },
        kind: 'select',
        options: [
          { value: 'password', label: { ar: 'البريد وكلمة المرور', en: 'Email and password' } },
          { value: 'otp', label: { ar: 'رمز إلى الجوال', en: 'A code to a mobile' } },
          { value: 'both', label: { ar: 'الاثنان في نموذج واحد', en: 'Both, in one form' } },
        ],
        hint: {
          ar: 'الرمز يحتاج تفعيل الرسائل و OTP من «الرسائل». إن لم يكن مفعّلًا يبقى الدخول بكلمة المرور.',
          en: 'Codes need texting and OTP switched on under Messaging. Without them the password form stays.',
        },
      },
      {
        name: 'signupByCode',
        label: { ar: 'فتح حساب برمز الجوال', en: 'Open an account with a code' },
        kind: 'toggle',
        hint: {
          ar: 'رقم جديد يصبح عميلًا عند التحقق من الرمز، دون بريد أو كلمة مرور، وتنضم إليه طلباته كزائر',
          en: 'A new number becomes a customer once its code is verified — no email, no password — and its guest orders join it',
        },
      },
      {
        name: 'signupPrefixes',
        label: { ar: 'الأرقام المسموح بمراسلتها', en: 'Numbers that may be texted' },
        kind: 'lines',
        hint: {
          ar: 'بادئة في كل سطر، مثل ‎+9665‎ — لا تُرسل رموز لأرقام جديدة خارجها',
          en: 'One prefix per line, like +9665 — no code goes to a new number outside them',
        },
      },
      {
        name: 'maxCodeRequestsPerIp',
        label: { ar: 'حد الطلبات من عنوان واحد في الساعة', en: 'Code requests per address, per hour' },
        kind: 'number',
      },
      // Google and Apple (ADR-117): public ids only — no secret is kept here.
      {
        name: 'googleEnabled',
        label: { ar: 'الدخول بحساب Google', en: 'Sign in with Google' },
        kind: 'toggle',
      },
      {
        name: 'googleClientId',
        label: { ar: 'معرّف العميل من Google', en: 'Google client ID' },
        kind: 'text',
        placeholder: { ar: '…apps.googleusercontent.com', en: '…apps.googleusercontent.com' },
        hint: {
          ar: 'من Google Cloud ← بيانات الاعتماد ← معرّف عميل OAuth (تطبيق ويب). أضف عنوان الرجوع: عنوان متجرك ثم ‎/api/auth/google/callback',
          en: 'From Google Cloud → Credentials → OAuth client ID (web application). Add the redirect URI: your store address followed by /api/auth/google/callback',
        },
      },
      {
        name: 'appleEnabled',
        label: { ar: 'الدخول بحساب Apple', en: 'Sign in with Apple' },
        kind: 'toggle',
      },
      {
        name: 'appleClientId',
        label: { ar: 'معرّف الخدمة من Apple', en: 'Apple Services ID' },
        kind: 'text',
        placeholder: { ar: 'com.example.web', en: 'com.example.web' },
        hint: {
          ar: 'من حساب Apple للمطورين ← المعرّفات ← Services ID مع «Sign in with Apple». أضف نطاق متجرك وعنوان الرجوع: عنوان متجرك ثم ‎/api/auth/apple/callback',
          en: 'From Apple Developer → Identifiers → a Services ID with Sign in with Apple. Add your store domain and the return URL: your store address followed by /api/auth/apple/callback',
        },
      },
      // Staff two-step sign-in (ADR-116).
      {
        name: 'staffTwoFactor',
        label: { ar: 'التحقق بخطوتين للموظفين', en: 'Two-step sign-in for staff' },
        kind: 'select',
        options: [
          { value: 'off', label: { ar: 'متوقف', en: 'Off' } },
          { value: 'optional', label: { ar: 'اختياري لكل موظف', en: 'Each person’s choice' } },
          { value: 'required', label: { ar: 'إلزامي للجميع', en: 'Required for everyone' } },
        ],
        hint: {
          ar: 'رمز من تطبيق المصادقة بعد كلمة المرور. «إلزامي»: من لم يفعّله يُعدّه عند دخوله القادم. كلٌّ يديره من اسمه أسفل القائمة',
          en: 'A code from an authenticator app after the password. Required: anyone without it sets it up at their next sign-in. Each person manages theirs from their name at the foot of the menu',
        },
      },
    ],
  },
  {
    // The dashboard's monthly goals (ADR-121).
    key: 'reports.targets',
    title: { ar: 'هدف الشهر', en: 'Monthly goal' },
    subtitle: { ar: 'يظهر في لوحة البداية مع الوتيرة المطلوبة كل يوم', en: 'Shown on the overview, with the pace each day needs' },
    fields: [
      { name: 'monthlyRevenue', label: { ar: 'مبيعات الشهر (ر.س)', en: 'Sales a month (SAR)' }, kind: 'money', hint: { ar: '0 بلا هدف', en: '0 for no goal' } },
      { name: 'monthlyOrders', label: { ar: 'طلبات الشهر', en: 'Orders a month' }, kind: 'number', hint: { ar: '0 بلا هدف', en: '0 for no goal' } },
    ],
  },
  {
    key: 'returns.policy',
    title: { ar: 'سياسة الإرجاع', en: 'Returns policy' },
    fields: [
      {
        name: 'windowDays',
        label: { ar: 'مدة الإرجاع بالأيام', en: 'Return window (days)' },
        kind: 'number',
        hint: { ar: 'من تاريخ التوصيل', en: 'Counted from delivery' },
      },
      {
        name: 'pickupBooking',
        label: { ar: 'استلام المرتجع من العميل', en: 'Collecting a return from the customer' },
        kind: 'select',
        options: [
          { value: 'manual', label: { ar: 'بزر «احجز الاستلام» في كل مرتجع', en: 'With a “Book pickup” button on each return' } },
          { value: 'onApproval', label: { ar: 'تلقائيًا عند قبول المرتجع', en: 'Automatically when the return is approved' } },
          { value: 'off', label: { ar: 'لا — يُرتَّب يدويًا', en: 'No — arranged by hand' } },
        ],
        hint: {
          ar: 'يحجز مندوبًا من منصة الشحن الموصولة (مثل OTO) لاستلام المرتجع من العميل. المرتجعات من الطلب نفسه تُستلم معًا.',
          en: 'Books a courier through the connected shipping provider (such as OTO) to collect it. Returns from one order travel together.',
        },
      },
      {
        name: 'returnShippingFee',
        label: { ar: 'رسوم شحن المرتجع على العميل (ر.س)', en: 'Return shipping the customer pays (SAR)' },
        kind: 'money',
        hint: {
          ar: 'تُخصم من المبلغ المسترد إذا أرجع العميل لأنه غيّر رأيه — مرة واحدة لكل طلب. لا تُخصم إن كان العيب أو الخطأ من المتجر. 0 للإرجاع المجاني.',
          en: 'Taken off the refund when the customer changed their mind — once per order. Never for a fault or mistake of the store’s. 0 for free returns.',
        },
      },
      {
        name: 'restockByDefault',
        label: { ar: 'إعادة للمخزون افتراضيًا', en: 'Restock by default' },
        kind: 'toggle',
        hint: {
          ar: 'يحدد فقط القيمة الأولية للخيار — القرار يبقى للموظف',
          en: 'Only sets what the checkbox starts at; staff still decide',
        },
      },
    ],
  },
  {
    // Things that arrive by link (ADR-077).
    key: 'digital.delivery',
    title: { ar: 'المنتجات الرقمية', en: 'Digital products' },
    subtitle: {
      ar: 'روابط تنزيل الملفات بعد الدفع — كم مرة ولكم يومًا',
      en: 'The download links sent after payment — how many times, and for how many days',
    },
    fields: [
      {
        name: 'downloadLimit',
        label: { ar: 'مرات التنزيل لكل ملف', en: 'Downloads per file' },
        kind: 'number',
        hint: { ar: 'لكل منتج في الطلب', en: 'For each product in an order' },
      },
      {
        name: 'downloadDays',
        label: { ar: 'مدة صلاحية الروابط (أيام)', en: 'Links work for (days)' },
        kind: 'number',
        hint: { ar: 'من تاريخ الدفع', en: 'Counted from payment' },
      },
    ],
  },
  {
    // Where each customer is with the store (ADR-103): the stage on the list, the profile, groups and campaigns.
    key: 'customers.stages',
    title: { ar: 'مراحل العملاء', en: 'Customer stages' },
    subtitle: {
      ar: 'متى يُعدّ العميل مهددًا بالفقد أو مفقودًا، وكم طلبًا يجعله وفيًّا',
      en: 'When a customer counts as at risk or lost, and how many orders make them loyal',
    },
    fields: [
      {
        name: 'atRiskDays',
        label: { ar: 'مهدد بالفقد بعد (أيام)', en: 'At risk after (days)' },
        kind: 'number',
        hint: { ar: 'منذ آخر طلب — من 14 يومًا', en: 'Since their last order — 14 or more' },
      },
      {
        name: 'lostDays',
        label: { ar: 'مفقود بعد (أيام)', en: 'Lost after (days)' },
        kind: 'number',
        hint: { ar: 'أكثر من مدة التهديد بالفقد', en: 'More than the at-risk days' },
      },
      {
        name: 'loyalOrders',
        label: { ar: 'وفيّ من (طلبات)', en: 'Loyal from (orders)' },
        kind: 'number',
        hint: { ar: 'طلبات مُباعة، وآخرها حديث', en: 'Sold orders, the last one recent' },
      },
    ],
  },
  {
    // Loyalty points (ADR-083): earned on delivered orders and reviews, exchanged into store credit.
    key: 'loyalty.config',
    title: { ar: 'نقاط الولاء', en: 'Loyalty points' },
    subtitle: {
      ar: 'نقاط على كل طلب يُسلَّم وكل تقييم يُنشر، يستبدلها العميل برصيد في المتجر',
      en: 'Points on every delivered order and published review, which customers exchange for store credit',
    },
    fields: [
      { name: 'enabled', label: { ar: 'تفعيل النقاط', en: 'Run a points programme' }, kind: 'toggle' },
      {
        name: 'earnPerRiyal',
        label: { ar: 'نقاط لكل ريال', en: 'Points per riyal' },
        kind: 'number',
        hint: { ar: 'على ما دُفع للمنتجات، دون الشحن والرسوم', en: 'On what was paid for the goods, not shipping or fees' },
      },
      {
        name: 'pointValue',
        label: { ar: 'قيمة النقطة (هللة)', en: 'A point is worth (halalas)' },
        kind: 'number',
        hint: { ar: '2 = كل 100 نقطة تساوي ريالين', en: '2 means 100 points are worth 2.00' },
      },
      {
        name: 'minimumToConvert',
        label: { ar: 'أقل عدد للاستبدال', en: 'Fewest points exchanged at once' },
        kind: 'number',
      },
      {
        name: 'reviewPoints',
        label: { ar: 'نقاط التقييم المنشور', en: 'Points for a published review' },
        kind: 'number',
        hint: { ar: 'مرة لكل تقييم — 0 لإيقافها', en: 'Once per review — 0 turns it off' },
      },
      {
        name: 'tiers',
        label: { ar: 'المستويات', en: 'Tiers' },
        kind: 'tiers',
        hint: {
          ar: 'حتى أربعة، يصلها العميل بما أنفقه في آخر 12 شهرًا، وكلٌّ يضاعف نقاطه بنسبته (150٪ = مرة ونصف)',
          en: 'Up to four, reached by what a customer spent in the last 12 months; each multiplies their points by its rate (150% = one and a half times)',
        },
      },
    ],
  },
  {
    // Refer a friend (ADR-114): each customer's link and code — a friend's first order off, and thanks on delivery.
    key: 'referrals.config',
    title: { ar: 'ادعُ صديقًا', en: 'Refer a friend' },
    subtitle: {
      ar: 'لكل عميل رابط ورمز يهديهما لأصدقائه: خصم على طلبهم الأول، وشكر للعميل حين يُسلَّم',
      en: 'Every customer gets a link and code for friends: a discount on their first order, and thanks for the customer once it is delivered',
    },
    fields: [
      { name: 'enabled', label: { ar: 'تفعيل الدعوات', en: 'Run invitations' }, kind: 'toggle' },
      {
        name: 'friendPercent',
        label: { ar: 'خصم الصديق (%)', en: 'The friend’s discount (%)' },
        kind: 'number',
        hint: { ar: 'على طلبه الأول فقط — من 5 إلى 50', en: 'On their first order only — 5 to 50' },
      },
      {
        name: 'minOrderTotal',
        label: { ar: 'أقل قيمة لطلب الصديق', en: 'The friend’s smallest order' },
        kind: 'money',
        hint: { ar: 'اتركه صفرًا بلا حد أدنى', en: 'Leave at zero for no minimum' },
      },
      {
        name: 'rewardKind',
        label: { ar: 'الشكر', en: 'The thanks' },
        kind: 'select',
        options: [
          { value: 'CREDIT', label: { ar: 'رصيد في المتجر', en: 'Store credit' } },
          { value: 'POINTS', label: { ar: 'نقاط ولاء', en: 'Loyalty points' } },
        ],
        hint: { ar: 'النقاط تحتاج برنامج النقاط مفعّلًا، وإلا يكون رصيدًا', en: 'Points need the points programme on; otherwise it is credit' },
      },
      { name: 'rewardCredit', label: { ar: 'الرصيد لكل صديق', en: 'Credit per friend' }, kind: 'money' },
      { name: 'rewardPoints', label: { ar: 'النقاط لكل صديق', en: 'Points per friend' }, kind: 'number' },
    ],
  },
  {
    // A share of each delivered order back as store credit (ADR-081).
    key: 'wallet.cashback',
    title: { ar: 'الاسترداد النقدي', en: 'Cashback' },
    subtitle: {
      ar: 'نسبة من كل طلب تُضاف إلى رصيد العميل عند التسليم، ويصرفها في طلب قادم',
      en: 'A share of each order added to the customer’s balance once it is delivered, to spend next time',
    },
    fields: [
      {
        name: 'percent',
        label: { ar: 'النسبة (%)', en: 'Percent back' },
        kind: 'number',
        hint: { ar: '0 لإيقافه — بحد أقصى 50', en: '0 turns it off — at most 50' },
      },
      {
        name: 'maxPerOrder',
        label: { ar: 'الحد الأقصى لكل طلب', en: 'Most one order earns' },
        kind: 'money',
        hint: { ar: 'اتركه صفرًا بلا حد', en: 'Leave at zero for no limit' },
      },
    ],
  },
  {
    key: 'reviews.policy',
    title: { ar: 'سياسة التقييمات', en: 'Reviews policy' },
    fields: [
      {
        name: 'autoApprove',
        label: { ar: 'نشر التقييمات تلقائيًا', en: 'Publish reviews automatically' },
        kind: 'toggle',
        hint: {
          ar: 'صندوق تقييمات بلا مراجعة يصبح هدفًا للسبام خلال أيام',
          en: 'An unmoderated review box becomes a spam target within days',
        },
      },
      {
        name: 'requireVerifiedPurchase',
        label: { ar: 'يشترط شراء موثّق', en: 'Require a verified purchase' },
        kind: 'toggle',
      },
      {
        name: 'usageQuestion',
        label: { ar: 'سؤال في نموذج التقييم', en: 'A question on the review form' },
        kind: 'bilingual',
        hint: {
          ar: 'مثل «ماذا صنعت منه؟» — يظهر فقط حين تُضاف إجابات',
          en: 'For example "What did you make?" — asked only once it has answers',
        },
      },
      {
        name: 'usageOptions',
        label: { ar: 'الإجابات', en: 'Its answers' },
        kind: 'choices',
        hint: {
          ar: 'الرمز يُحفظ مع التقييم ولا يظهر للعميل — لا تغيّره بعد الاستخدام',
          en: 'The code is stored with each review and never shown — keep it once used',
        },
      },
    ],
  },
  {
    key: 'email.config',
    title: { ar: 'البريد الإلكتروني', en: 'Email' },
    subtitle: { ar: 'من يصل البريد للعميل وكيف يُوقَّع', en: 'Who mail comes from, and how it signs off' },
    fields: [
      {
        name: 'senderName',
        label: { ar: 'اسم المرسل', en: 'Sender name' },
        kind: 'text',
        hint: { ar: 'اتركه فارغًا لاستخدام اسم المتجر', en: "Leave empty to use the store's name" },
      },
      {
        name: 'senderEmail',
        label: { ar: 'بريد المرسل', en: 'Sender address' },
        kind: 'text',
        hint: {
          ar: 'اتركه فارغًا لاستخدام العنوان المهيأ في الخادم',
          en: "Leave empty to use the mail driver's configured address",
        },
      },
      { name: 'replyTo', label: { ar: 'بريد الرد', en: 'Reply-to address' }, kind: 'text' },
      { name: 'logoUrl', label: { ar: 'شعار البريد', en: 'Email logo' }, kind: 'text' },
      {
        name: 'footerText',
        label: { ar: 'تذييل الرسالة', en: 'Email footer' },
        kind: 'bilingualText',
        hint: { ar: 'يظهر أسفل كل رسالة', en: 'Appears at the foot of every message' },
      },
    ],
  },
  {
    key: 'invoice.config',
    title: { ar: 'الفاتورة', en: 'Invoice' },
    subtitle: {
      ar: 'ترقيم الفواتير والملاحظة أسفلها',
      en: 'How invoices are numbered, and the note under the totals',
    },
    fields: [
      {
        name: 'numberPrefix',
        label: { ar: 'بادئة الرقم', en: 'Number prefix' },
        kind: 'text',
        hint: {
          ar: 'مثل INV — يظهر قبل رقم كل فاتورة',
          en: 'INV, say — it leads every invoice number',
        },
      },
      {
        name: 'creditNotePrefix',
        label: { ar: 'بادئة رقم الإشعار الدائن', en: 'Credit note prefix' },
        kind: 'text',
        hint: {
          ar: 'مثل CN — للإشعارات الدائنة عند الاسترداد أو الإلغاء، بتسلسل مستقل',
          en: 'CN, say — credit notes for refunds and cancellations run their own series',
        },
      },
      {
        name: 'zatcaMode',
        label: { ar: 'الفوترة الإلكترونية (ZATCA)', en: 'E-invoicing (ZATCA)' },
        kind: 'select',
        options: [
          {
            value: 'none',
            label: { ar: 'بدون — إيصال فقط', en: 'None — a plain receipt' },
          },
          {
            value: 'phase1',
            label: { ar: 'المرحلة الأولى — رمز QR', en: 'Phase 1 — the QR code' },
          },
          {
            value: 'phase2',
            label: { ar: 'المرحلة الثانية — QR وسلسلة الفواتير', en: 'Phase 2 — QR and the invoice chain' },
          },
        ],
        hint: {
          ar:
            'اختر «بدون» إن لم يكن لدى المتجر رقم ضريبي أو سجل تجاري بعد — تُصدر إيصالات لا فواتير ضريبية. ' +
            'المرحلة الثانية تضيف معرّفًا وتسلسلًا وربطًا بالفاتورة السابقة؛ الربط المباشر بمنصة فاتورة غير مفعّل بعد.',
          en:
            "Pick None while the store has no VAT or CR number — it issues receipts, not tax invoices. " +
            'Phase 2 adds a UUID, a counter and the hash of the previous invoice; reporting to ZATCA’s own platform is not connected yet.',
        },
      },
      {
        name: 'footerNote',
        label: { ar: 'ملاحظة أسفل الفاتورة', en: 'Footer note' },
        kind: 'bilingualText',
        hint: {
          ar: 'تُطبع أسفل الإجماليات بلغة المتجر',
          en: "Printed under the totals in the store's language",
        },
      },
    ],
  },
  {
    key: 'support.config',
    title: { ar: 'الدعم والتذاكر', en: 'Support tickets' },
    subtitle: {
      ar: 'هل يستطيع العميل فتح تذكرة، وما الأسباب المعروضة عليه',
      en: 'Whether a customer can open a ticket, and the reasons they choose from',
    },
    fields: [
      {
        name: 'enabled',
        label: { ar: 'استقبال التذاكر', en: 'Accept tickets' },
        kind: 'toggle',
        hint: {
          ar: 'مغلق افتراضيًا — متجر بلا من يردّ لا ينبغي أن يدعو للمراسلة',
          en: 'Off by default: a store with nobody to answer should not invite letters',
        },
      },
      {
        name: 'guests',
        label: { ar: 'استقبال رسائل الزوار', en: 'Take messages from guests' },
        kind: 'toggle',
        hint: {
          ar: 'من لا حساب له يكتب من صفحة «راسلنا» ويصله الرابط والردود بالبريد (ADR-041)',
          en: 'Visitors without an account write from the "write to us" page; the link and your replies reach them by email (ADR-041)',
        },
      },
    ],
  },
  {
    key: 'trade.config',
    title: { ar: 'حسابات التجّار', en: 'Trade accounts' },
    subtitle: {
      ar: 'بيع الشركات بقوائم أسعارها الخاصة (ADR-045)',
      en: 'Selling to businesses at their own price lists (ADR-045)',
    },
    fields: [
      {
        name: 'enabled',
        label: { ar: 'تفعيل أسعار التجّار', en: 'Trade prices on' },
        kind: 'toggle',
        hint: {
          ar: 'عند الإيقاف يشتري كل حساب تاجر بسعر التجزئة',
          en: 'Off, every trade account buys at retail',
        },
      },
      {
        name: 'applications',
        label: { ar: 'استقبال طلبات حسابات التجّار', en: 'Take trade applications' },
        kind: 'toggle',
        hint: {
          ar: 'يتقدّم العميل من صفحة حسابه التجاري بمستنداته، وتراجعها في «طلبات حسابات التجّار» (ADR-046)',
          en: 'Customers apply from their business account page, with their documents; you answer in Trade applications (ADR-046)',
        },
      },
      {
        name: 'applicationPrefix',
        label: { ar: 'بادئة رقم الطلب', en: 'Application number prefix' },
        kind: 'text',
        hint: {
          ar: 'حروف وأرقام، مثل TRD (الافتراضي) — لا يُعيد ترقيم الطلبات السابقة',
          en: 'Letters and digits, TRD unless you say otherwise — earlier applications keep their numbers',
        },
      },
      {
        name: 'businessTypes',
        label: { ar: 'أنواع النشاط', en: 'Business types' },
        kind: 'choices',
        hint: {
          ar: 'فارغة: يعرض النموذج قائمة المنصّة (متجر، ورشة، مصمّم، جملة، ضيافة، أخرى)',
          en: "Empty: the form offers the platform's list (shop, workshop, designer, wholesale, hospitality, other)",
        },
      },
      {
        name: 'volumes',
        label: { ar: 'أحجام الشراء الشهرية', en: 'Monthly volume bands' },
        kind: 'choices',
        hint: {
          ar: 'بما يناسب متجرك — أمتار أو كراتين أو ريالات. فارغة: شرائح بالريال',
          en: 'In what your store counts — metres, cartons, riyals. Empty: bands in riyals',
        },
      },
      {
        name: 'documents',
        label: { ar: 'المستندات المطلوبة', en: 'Documents asked for' },
        kind: 'choices',
        hint: {
          ar: 'PDF أو JPEG أو PNG حتى ١٠ ميغابايت لكل ملف. فارغة: السجل التجاري، شهادة الضريبة، مستند آخر',
          en: 'PDF, JPEG or PNG, up to 10 MB each. Empty: the CR certificate, the VAT certificate, another document',
        },
      },
      {
        name: 'requiredDocuments',
        label: { ar: 'رموز المستندات الإلزامية', en: 'Required document codes' },
        kind: 'lines',
        hint: {
          ar: 'رمز في كل سطر، من رموز المستندات أعلاه. فارغة: السجل التجاري (cr) إن كان من المستندات المطلوبة',
          en: 'One code per line, from the documents above. Empty: the CR certificate (cr), when it is one of them',
        },
      },
    ],
  },
  {
    key: 'catalog.display',
    title: { ar: 'عرض المنتجات', en: 'Product listing' },
    subtitle: {
      ar: 'كم منتجًا في الصفحة، وبأي ترتيب تبدأ',
      en: 'How many products a page shows, and the order it opens in',
    },
    fields: [
      {
        name: 'productsPerPage',
        label: { ar: 'منتجات في الصفحة', en: 'Products per page' },
        kind: 'number',
        hint: {
          ar: 'بين 4 و48 — الحد الأقصى يفرضه الخادم',
          en: 'Between 4 and 48; the API caps it regardless',
        },
      },
      {
        name: 'defaultSort',
        label: { ar: 'الترتيب الافتراضي', en: 'Default sort' },
        kind: 'select',
        options: [
          { value: 'newest', label: { ar: 'الأحدث', en: 'Newest' } },
          { value: 'price_asc', label: { ar: 'الأقل سعرًا', en: 'Price: low to high' } },
          { value: 'price_desc', label: { ar: 'الأعلى سعرًا', en: 'Price: high to low' } },
          { value: 'name_asc', label: { ar: 'الاسم أ–ي', en: 'Name A–Z' } },
        ],
      },
      {
        // How a category page opens (R3). Its own setting, because a
        // category's hand-picked order means nothing on the all-products page.
        name: 'categorySort',
        label: { ar: 'ترتيب صفحة التصنيف', en: 'Category page sort' },
        kind: 'select',
        hint: {
          ar: '«المميّز» هو الترتيب الذي تختاره لكل تصنيف من شاشة التصنيفات',
          en: '“Featured” is the order you set for each category on the Categories screen',
        },
        options: [
          { value: 'featured', label: { ar: 'المميّز', en: 'Featured' } },
          { value: 'bestselling', label: { ar: 'الأكثر مبيعًا', en: 'Best selling' } },
          { value: 'rating', label: { ar: 'الأعلى تقييمًا', en: 'Top rated' } },
          { value: 'newest', label: { ar: 'الأحدث', en: 'Newest' } },
          { value: 'price_asc', label: { ar: 'الأقل سعرًا', en: 'Price: low to high' } },
          { value: 'price_desc', label: { ar: 'الأعلى سعرًا', en: 'Price: high to low' } },
          { value: 'name_asc', label: { ar: 'الاسم أ–ي', en: 'Name A–Z' } },
        ],
      },
      {
        name: 'quantityGuide',
        label: { ar: 'كم أحتاج؟', en: 'How much do I need?' },
        kind: 'quantityGuide',
        hint: {
          ar: 'جدول تقديري لما يُباع بالقياس — «فستان ٣ م». يظهر على المنتجات التي تفعّله، بوحدتها. اختيار سطر يملأ الكمية',
          en: 'A rough table for what is sold by measure — "Dress 3 m". Shown on the products that switch it on, in their unit. Picking a row fills the quantity',
        },
      },
    ],
  },
  {
    key: 'company.legal',
    title: { ar: 'البيانات النظامية', en: 'Legal details' },
    subtitle: { ar: 'تظهر على الفواتير الضريبية', en: 'Printed on tax invoices' },
    fields: [
      {
        name: 'vatNumber',
        label: { ar: 'الرقم الضريبي', en: 'VAT registration number' },
        kind: 'text',
        hint: { ar: 'مطلوب على الفاتورة الضريبية السعودية', en: 'Required on a Saudi tax invoice' },
      },
      {
        name: 'commercialRegistration',
        label: { ar: 'السجل التجاري', en: 'Commercial registration' },
        kind: 'text',
      },
      { name: 'legalName', label: { ar: 'الاسم النظامي', en: 'Legal name' }, kind: 'bilingual' },
    ],
  },
  {
    key: 'shipping.config',
    title: { ar: 'الشحن', en: 'Shipping' },
    fields: [
      {
        name: 'senderAddress',
        label: { ar: 'عنوان الإرسال', en: 'Ship-from address' },
        kind: 'text',
        hint: {
          ar: 'يُطبع في خانة المرسل على ملصق الشحنة، مع اسم المتجر ورقمه',
          en: "Printed as the sender on every shipping label, with the store's name and phone",
        },
      },
      {
        name: 'freeShippingThreshold',
        label: { ar: 'حد الشحن المجاني', en: 'Free shipping over' },
        kind: 'money',
        hint: { ar: 'اتركه فارغًا لإلغاء العرض', en: 'Leave empty for no free-shipping offer' },
      },
      {
        name: 'showEstimatesOnProduct',
        label: { ar: 'إظهار مدة التوصيل في صفحة المنتج', en: 'Show delivery estimate on the product page' },
        kind: 'toggle',
        hint: {
          ar: '«اطلب اليوم — يصلك …» بالتواريخ، من أول طريقة توصيل لها مدة',
          en: '"Order today — arrives …" as dates, from the first delivery method with timing',
        },
      },
      {
        name: 'freeSampleDelivery',
        label: { ar: 'توصيل العيّنات وحدها مجانًا', en: 'Deliver samples on their own free' },
        kind: 'toggle',
        hint: {
          ar: 'طلب لا يضم إلا عيّنات مجانية يُوصَّل بأرخص طريقة توصيل دون رسوم؛ الطرق الأسرع بسعرها',
          en: 'An order of nothing but free samples goes by the cheapest delivery at no charge; faster ones keep their price',
        },
      },
    ],
  },
  {
    // What the ship dialog offers, and the tracking link it builds (ADR-061).
    key: 'shipping.carriers',
    title: { ar: 'شركات الشحن', en: 'Carriers' },
    fields: [
      {
        name: 'carriers',
        label: { ar: 'شركات الشحن', en: 'Carriers' },
        kind: 'lines',
        hint: {
          ar: 'شركة في كل سطر: الاسم، ثم « | » ورابط التتبع بوضع {number} مكان رقم الشحنة — مثل: SMSA Express | https://…?tracknumbers={number}',
          en: 'One per line: the name, then " | " and the tracking link with {number} where the tracking number goes — e.g. SMSA Express | https://…?tracknumbers={number}',
        },
      },
    ],
  },
  {
    // Every delivery date is counted on this calendar (R6).
    key: 'shipping.calendar',
    title: { ar: 'تقويم التوصيل', en: 'Delivery calendar' },
    subtitle: {
      ar: 'الأيام التي تُحسب عليها مواعيد التوصيل والاستلام',
      en: 'The days delivery and pickup dates are counted on',
    },
    fields: [
      {
        name: 'timeZone',
        label: { ar: 'المنطقة الزمنية', en: 'Time zone' },
        kind: 'text',
        hint: { ar: 'يُقرأ فيها آخر موعد للطلب — مثل Asia/Riyadh', en: 'Where order cutoffs are read — e.g. Asia/Riyadh' },
      },
      {
        name: 'weekendDays',
        label: { ar: 'العطلة الأسبوعية', en: 'Weekend' },
        kind: 'weekdays',
        hint: { ar: 'لا تجهيز ولا توصيل في هذه الأيام', en: 'Nothing is dispatched or delivered on these days' },
      },
      {
        name: 'holidays',
        label: { ar: 'الإجازات', en: 'Holidays' },
        kind: 'lines',
        hint: {
          ar: 'تاريخ في كل سطر: 09-23 كل عام، أو 2027-03-10 مرة واحدة — أضف أيام العيد كل عام',
          en: 'One per line: 09-23 every year, or 2027-03-10 once — add each year’s Eid days',
        },
      },
    ],
  },
];

/** The content pages the storefront links to, and what each one is for. */
export const CONTENT_PAGES: { slug: string; label: BilingualLabel }[] = [
  { slug: 'about', label: { ar: 'من نحن', en: 'About us' } },
  { slug: 'faq', label: { ar: 'الأسئلة الشائعة', en: 'Questions and answers' } },
  { slug: 'shipping', label: { ar: 'الشحن', en: 'Shipping' } },
  { slug: 'returns', label: { ar: 'الإرجاع', en: 'Returns' } },
  { slug: 'contact', label: { ar: 'تواصل معنا', en: 'Contact' } },
  { slug: 'privacy', label: { ar: 'سياسة الخصوصية', en: 'Privacy policy' } },
  { slug: 'terms', label: { ar: 'الشروط والأحكام', en: 'Terms of service' } },
];

/** Setting keys that have a real form; anything else falls to "advanced". */
export const DESCRIBED_KEYS = [
  ...SETTING_SECTIONS.map((section) => section.key),
  'features.flags',
  'home.layout',
  'pages.content',
];
